import { GradientParser } from '../../lib/gradient';
import { DPR } from '../constants';
import { charWidth } from './text';

// Extended stroke layer structure:
// [{
//     stroke: '#f00',
//     width: 20,
//     opacity: 1,
//     blur: 0,
//     offset: { x: 0, y: 0 },
//     lineCap: 'round',
//     lineJoin: 'round',
//     miterLimit: 4
// }, {
//     stroke: 'rgb(255, 244, 244)',
//     width: 40,
//     opacity: 0.8,
//     blur: 2,
//     offset: { x: 2, y: 2 }
// }, {
//     stroke: 'linear-gradient(45deg, #ff0000, #0000ff)',
//     width: 10,
//     opacity: 0.9,
//     blur: 1
// }]
export function serializeStrokeLayers(strokes) {
  if (!strokes || strokes.length === 0) return '';

  return strokes
    .map((layer) => {
      const stroke = layer.stroke || '';
      const width = layer.width || 0;
      const opacity = layer.opacity ?? 1;
      const blur = layer.blur || 0;
      const offsetX = layer.offset?.x || 0;
      const offsetY = layer.offset?.y || 0;
      const lineCap = layer.lineCap || 'round';
      const lineJoin = layer.lineJoin || 'round';
      const miterLimit = layer.miterLimit || 4;

      return `${stroke}|${width}|${opacity}|${blur}|${offsetX}|${offsetY}|${lineCap}|${lineJoin}|${miterLimit}`;
    })
    .join('~');
}
// Support for dual-layer stroke rendering
export const drawMultiLayerStroke = (
  renderContext,
  context,
  text,
  strokes,
  textX,
  textY,
  lineContext,
) => {
  const { w, h, letterSpacing } = lineContext;
  const layers = deserializeStrokeLayers(strokes);
  if (!layers || layers.length === 0) return;

  const sortedLayers = [...layers].reverse();

  sortedLayers.forEach((layer) => {
    if (layer.stroke && layer.width) {
      // Save context state
      context.save();

      // Apply opacity
      if (layer.opacity !== undefined && layer.opacity !== 1) {
        context.globalAlpha = layer.opacity;
      }

      // Apply blur effect
      if (layer.blur > 0) {
        const dpr = renderContext.dpr || DPR;
        const { scale } = renderContext.providers;
        const scaleValue = scale?.scale || 1;
        context.filter = `blur(${layer.blur * dpr * scaleValue}px)`;
      }

      // Calculate position with offset
      const finalX = textX + (layer.offset?.x || 0);
      const finalY = textY + (layer.offset?.y || 0);

      // Apply stroke properties
      context.lineWidth = layer.width;
      context.lineCap = layer.lineCap || 'round';
      context.lineJoin = layer.lineJoin || 'round';
      context.miterLimit = layer.miterLimit || 4;

      // Check if stroke is a gradient
      if (GradientParser.is(layer.stroke)) {
        // Apply gradient for stroke
        GradientParser.create(
          context,
          layer.stroke,
          { x: textX, y: textY, w: w, h: h },
          (gradient) => {
            context.strokeStyle = gradient;

            // Use manual character spacing for consistency with text rendering
            let currentX = finalX;
            const chars = Array.from(text); // Properly handles emoji and surrogate pairs

            for (let i = 0; i < chars.length; i++) {
              const char = chars[i];
              context.strokeText(char, currentX, finalY);
              const w = charWidth.calculate(char, lineContext.fontValue);
              const justifyExtraSpace = lineContext.justifyExtraSpace || 0;
              const justifyApplyOnSpacesOnly = lineContext.justifyApplyOnSpacesOnly !== false;
              const jFirst = lineContext.justifyFirstNonSpace ?? 0;
              const jLast = lineContext.justifyLastNonSpace ?? Number.MAX_SAFE_INTEGER;
              const insideEligible = i >= jFirst && i < jLast;
              const shouldApplyExtra =
                justifyExtraSpace > 0 &&
                i < chars.length - 1 &&
                insideEligible &&
                (!justifyApplyOnSpacesOnly || char === ' ');
              const extra = shouldApplyExtra ? justifyExtraSpace : 0;
              currentX += w + (i < chars.length - 1 ? letterSpacing || 0 : 0) + extra;
            }
          },
        );
      } else {
        // Apply solid color stroke
        context.strokeStyle = layer.stroke;

        // Use manual character spacing for consistency with text rendering
        let currentX = finalX;
        const chars = Array.from(text); // Properly handles emoji and surrogate pairs

        for (let i = 0; i < chars.length; i++) {
          const char = chars[i];
          context.strokeText(char, currentX, finalY);
          const w = charWidth.calculate(char, lineContext.fontValue);
          const justifyExtraSpace = lineContext.justifyExtraSpace || 0;
          const justifyApplyOnSpacesOnly = lineContext.justifyApplyOnSpacesOnly !== false;
          const jFirst = lineContext.justifyFirstNonSpace ?? 0;
          const jLast = lineContext.justifyLastNonSpace ?? Number.MAX_SAFE_INTEGER;
          const insideEligible = i >= jFirst && i < jLast;
          const shouldApplyExtra =
            justifyExtraSpace > 0 &&
            i < chars.length - 1 &&
            insideEligible &&
            (!justifyApplyOnSpacesOnly || char === ' ');
          const extra = shouldApplyExtra ? justifyExtraSpace : 0;
          currentX += w + (i < chars.length - 1 ? letterSpacing || 0 : 0) + extra;
        }
      }

      // Restore context state
      context.restore();
    }
  });
};

export function resolveTextStrokeProps(node) {
  const strokes = resolveTextStrokes(node);
  if (!strokes) return null;
  const layers = deserializeStrokeLayers(strokes);
  if (!layers || !layers.length) return null;
  const firstLayer = layers[0];
  if (!firstLayer.width) return null;
  return {
    width: firstLayer.width,
    stroke: firstLayer.stroke,
  };
}

export function resolveTextStrokes(layer, node) {
  // If node has serialized strokes string, deserialize it
  if (layer.strokes && typeof layer.strokes === 'string') {
    return layer.strokes;
  }

  // Fallback to legacy single stroke properties
  const stroke = layer.stroke ?? node?.stroke;
  const width = layer.strokeWidth ?? node?.strokeWidth;
  if (stroke && width) {
    return serializeStrokeLayers([
      {
        stroke: stroke,
        width: width,
        opacity: layer.strokeOpacity ?? node?.strokeOpacity ?? 1,
        blur: layer.strokeBlur ?? node?.strokeBlur ?? 0,
        offset: {
          x: layer.strokeOffsetX ?? node?.strokeOffsetX ?? 0,
          y: layer.strokeOffsetY ?? node?.strokeOffsetY ?? 0,
        },
        lineCap: layer.strokeLineCap ?? node?.strokeLineCap ?? 'round',
        lineJoin: layer.strokeLineJoin ?? node?.strokeLineJoin ?? 'round',
        miterLimit: layer.strokeMiterLimit ?? node?.strokeMiterLimit ?? 4,
      },
    ]);
  }

  return '';
}

export function deserializeStrokeLayers(strokeString) {
  if (!strokeString || typeof strokeString !== 'string') return [];

  try {
    return strokeString
      .split('~')
      .map((layerStr) => {
        if (!layerStr || typeof layerStr !== 'string') return null;

        const parts = layerStr.split('|');
        if (parts.length < 2) return null;

        const stroke = parts[0]?.trim();
        const width = parseFloat(parts[1]);

        if (!stroke || isNaN(width) || width <= 0) return null;

        return {
          stroke: stroke,
          width: width,
          opacity: parts[2] !== undefined ? parseFloat(parts[2]) : 1,
          blur: parts[3] !== undefined ? parseFloat(parts[3]) : 0,
          offset: {
            x: parts[4] !== undefined ? parseFloat(parts[4]) : 0,
            y: parts[5] !== undefined ? parseFloat(parts[5]) : 0,
          },
          lineCap: parts[6] || 'round',
          lineJoin: parts[7] || 'round',
          miterLimit: parts[8] !== undefined ? parseFloat(parts[8]) : 4,
        };
      })
      .filter((layer) => layer && layer.stroke && layer.width > 0);
  } catch (error) {
    console.warn('Failed to deserialize stroke layers:', error);
    return [];
  }
}
